import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Network, ZoomIn, ZoomOut, Printer, Maximize, FileDown, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import OrgNode from "./_OrgNode";

export default function OrgchartPage() {
  const { companyParam, companies, selected, select } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [zoom, setZoom] = useState(1);
  const [showResp, setShowResp] = useState(true);
  const [showCounts, setShowCounts] = useState(true);
  const [orientation, setOrientation] = useState("landscape");
  const [pageSize, setPageSize] = useState("A4");
  const [sectorFilter, setSectorFilter] = useState("all");
  const containerRef = useRef(null);

  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (companyParam) params.company_id = companyParam;
        const { data: d } = await api.get("/sectors/orgchart", { params });
        setData(d);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, [companyParam]);

  // Build a subtree rooted at a specific sector_id — walks the normalized roots
  const roots = (() => {
    if (!data) return [];
    if (sectorFilter === "all") return data.roots;
    const find = (nodes) => {
      for (const n of nodes) {
        if (n.id === sectorFilter) return n;
        const hit = find(n.children || []);
        if (hit) return hit;
      }
      return null;
    };
    const hit = find(data.roots);
    return hit ? [hit] : [];
  })();

  // Flatten sectors for the filter dropdown
  const allSectors = (() => {
    if (!data) return [];
    const out = [];
    const walk = (nodes, depth = 0) => {
      nodes.forEach((n) => { out.push({ ...n, depth }); walk(n.children || [], depth + 1); });
    };
    walk(data.roots || []);
    return out;
  })();

  const toggleCollapse = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const onMouseDown = (e) => {
    if (e.target.closest("button, input, select, [role='combobox']")) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, x: pan.x, y: pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.x + (e.clientX - dragRef.current.startX),
      y: dragRef.current.y + (e.clientY - dragRef.current.startY),
    });
  };
  const onMouseUp = () => { dragRef.current = null; };

  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handlePrint = () => {
    document.documentElement.setAttribute("data-print-orient", orientation);
    document.documentElement.setAttribute("data-print-size", pageSize);
    const area = document.getElementById("org-print-area");
    if (area) {
      const rect = area.getBoundingClientRect();
      // A4 landscape inner area at 96dpi ~ 1080x744; A3 landscape ~ 1530x1056
      const sizes = {
        A4: { landscape: [1080, 744], portrait: [744, 1080] },
        A3: { landscape: [1530, 1056], portrait: [1056, 1530] },
      };
      const [targetW, targetH] = sizes[pageSize][orientation];
      const logicalW = rect.width / zoom;
      const logicalH = rect.height / zoom;
      const fit = Math.min(targetW / logicalW, targetH / logicalH, 1);
      document.documentElement.style.setProperty("--print-fit", fit.toFixed(3));
    }
    setTimeout(() => window.print(), 80);
  };

  const exportPNG = async () => {
    const area = document.getElementById("org-print-area");
    if (!area) return;
    try {
      const { default: html2canvas } = await import("html2canvas");
      // temporarily reset zoom/pan to capture
      const prevTransform = area.style.transform;
      area.style.transform = "scale(1)";
      const canvas = await html2canvas(area, { backgroundColor: "#ffffff", scale: 2 });
      area.style.transform = prevTransform;
      const link = document.createElement("a");
      link.download = `organograma-${data?.company?.name || "grupo"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Imagem PNG exportada");
    } catch (e) {
      toast.error("Falha ao exportar imagem");
    }
  };

  const exportPDF = async () => {
    const area = document.getElementById("org-print-area");
    if (!area) return;
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"), import("jspdf"),
      ]);
      const prevTransform = area.style.transform;
      area.style.transform = "scale(1)";
      const canvas = await html2canvas(area, { backgroundColor: "#ffffff", scale: 2 });
      area.style.transform = prevTransform;
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: orientation === "landscape" ? "l" : "p",
        unit: "mm", format: pageSize.toLowerCase(),
      });
      const pw = pdf.internal.pageSize.getWidth() - 16;
      const ph = pdf.internal.pageSize.getHeight() - 16;
      const ratio = canvas.width / canvas.height;
      let w = pw, h = pw / ratio;
      if (h > ph) { h = ph; w = ph * ratio; }
      pdf.setFontSize(11);
      pdf.text(`Organograma — ${data?.company?.name || "Grupo ALCON"}`, 8, 8);
      pdf.setFontSize(8);
      pdf.text(`Emitido em ${new Date().toLocaleString("pt-BR")} · ${pageSize} ${orientation === "landscape" ? "paisagem" : "retrato"}`, 8, 13);
      pdf.addImage(img, "PNG", 8 + (pw - w) / 2, 16, w, h);
      pdf.save(`organograma-${data?.company?.name || "grupo"}-${pageSize}.pdf`);
      toast.success(`PDF ${pageSize} exportado`);
    } catch (e) {
      toast.error("Falha ao exportar PDF");
    }
  };

  const exportExcel = async () => {
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const r = await api.get("/sectors/orgchart/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `organograma-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch (e) {
      toast.error("Falha ao exportar Excel");
    }
  };

  return (
    <div className="space-y-5" data-testid="orgchart-page">
      {/* Hero header — hidden on print */}
      <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/25 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-6 text-white print:hidden">
        <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]" />
        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-medium flex items-center gap-1.5">
              <Network className="h-3 w-3" /> Estrutura Organizacional
            </div>
            <h1 className="mt-1 font-display text-3xl md:text-[34px] font-semibold tracking-tight">
              Organograma
            </h1>
            <p className="mt-2 text-[13px] text-white/70 max-w-[680px]">
              Visualização hierárquica dos setores com indicadores de quadro aprovado vs. atual.
              Filtre pela empresa, expanda os ramos e imprima em A4.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={selected || "all"} onValueChange={(v) => select(v)}>
              <SelectTrigger className="w-[260px] border-white/20 bg-white/5 text-white" data-testid="org-company-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as empresas (consolidado)</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pageSize} onValueChange={setPageSize}>
              <SelectTrigger className="w-[90px] border-white/20 bg-white/5 text-white" data-testid="org-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A4">A4</SelectItem>
                <SelectItem value="A3">A3</SelectItem>
              </SelectContent>
            </Select>
            <Select value={orientation} onValueChange={setOrientation}>
              <SelectTrigger className="w-[130px] border-white/20 bg-white/5 text-white" data-testid="org-orient">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="landscape">Paisagem</SelectItem>
                <SelectItem value="portrait">Retrato</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="w-[220px] border-white/20 bg-white/5 text-white" data-testid="org-sector-filter">
                <SelectValue placeholder="Todos os setores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os setores</SelectItem>
                {allSectors.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {"— ".repeat(s.depth)}{s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={exportPDF} variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/15" data-testid="org-pdf-btn">
              <FileDown className="h-4 w-4 mr-1.5" /> PDF
            </Button>
            <Button onClick={exportPNG} variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/15" data-testid="org-png-btn">
              <ImageIcon className="h-4 w-4 mr-1.5" /> Imagem
            </Button>
            <Button onClick={exportExcel} variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/15" data-testid="org-xlsx-btn">
              <FileDown className="h-4 w-4 mr-1.5" /> Excel
            </Button>
            <Button onClick={handlePrint} className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]" data-testid="org-print-btn">
              <Printer className="h-4 w-4 mr-1.5" /> Imprimir
            </Button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {data && (
        <div className="rounded-xl border border-border bg-white p-5 print:rounded-none print:border-0 print:p-3 print:shadow-none" data-testid="org-summary">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#8A6B2B] font-semibold">
                Empresa
              </div>
              <div className="font-display text-2xl font-semibold mt-1 text-slate-900 print:text-[18px]">
                {data.company.name}
              </div>
              <div className="hidden print:block text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mt-1">
                Organograma Organizacional · Emitido em {new Date().toLocaleString("pt-BR")}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 text-right print:gap-2">
              <Stat label="Setores" value={data.totals.sectors} />
              <Stat label="Aprovado" value={data.totals.aprovado} />
              <Stat label="Atual" value={data.totals.atual} />
              <Stat
                label="Diferença"
                value={`${data.totals.diferenca >= 0 ? "+" : ""}${data.totals.diferenca}`}
                tone={data.totals.diferenca < 0 ? "rose" : "emerald"}
              />
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="rounded-lg border border-border bg-white p-2.5 flex items-center gap-2 flex-wrap print:hidden">
        <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.min(2, z + 0.1))} data-testid="org-zoom-in">
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.max(0.4, z - 0.1))} data-testid="org-zoom-out">
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <Button variant="outline" size="sm" onClick={resetView} data-testid="org-fit">
          <Maximize className="h-3.5 w-3.5 mr-1" /> Reset
        </Button>
        <span className="text-[11.5px] text-muted-foreground tabular-nums px-2">{Math.round(zoom * 100)}%</span>
        <div className="ml-auto flex items-center gap-3 text-[11.5px]">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showResp} onChange={(e) => setShowResp(e.target.checked)} className="accent-[#C5A059]" data-testid="org-show-resp" />
            <span>Responsáveis</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showCounts} onChange={(e) => setShowCounts(e.target.checked)} className="accent-[#C5A059]" data-testid="org-show-counts" />
            <span>Headcount</span>
          </label>
        </div>
      </div>

      {/* Canvas */}
      {loading ? (
        <Skeleton className="h-[520px] rounded-xl" />
      ) : !data || roots.length === 0 ? (
        <EmptyState companyName={data?.company?.name} />
      ) : (
        <div
          ref={containerRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          data-testid="org-canvas"
          className="rounded-xl border border-border bg-gradient-to-b from-[#F6F4EE] to-white overflow-hidden cursor-grab active:cursor-grabbing select-none print:rounded-none print:border-0 print:overflow-visible print:bg-white"
          style={{ minHeight: 520 }}
        >
          <div
            className="p-10 print:p-2"
            id="org-print-area"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "top center",
              transition: dragRef.current ? "none" : "transform 0.2s ease-out",
            }}
          >
            <div className="flex flex-col items-center gap-12 print:gap-6">
              {roots.map((r) => (
                <OrgNode
                  key={r.id}
                  node={r}
                  collapsed={collapsed}
                  onToggle={toggleCollapse}
                  showResp={showResp}
                  showCounts={showCounts}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Print-only footer */}
      <div className="hidden print:block text-[9px] text-slate-500 text-center py-2 border-t border-slate-200 mt-4">
        TheoKratos RH · Gerado em {new Date().toLocaleString("pt-BR")}
      </div>

      <style>{`
        @media print {
          @page { size: ${pageSize} ${orientation}; margin: 8mm; }
          html, body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          aside, [data-sidebar], header, nav, .print\\:hidden { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; }
          [data-testid="orgchart-page"] > * { break-inside: avoid; }
          [data-testid="orgchart-page"] { padding: 0 !important; margin: 0 !important; }
          /* Reset interactive container so the whole chart is visible */
          [data-testid="org-canvas"] {
            border: 0 !important; background: white !important;
            min-height: auto !important; overflow: visible !important;
            cursor: default !important;
          }
          #org-print-area {
            transform: scale(var(--print-fit, 0.85)) !important;
            transform-origin: top left !important;
            page-break-inside: avoid;
          }
          /* Stack roots vertically/horizontally tightly in print */
          #org-print-area > .flex { gap: 1.25rem !important; }
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const toneCls = tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-600" : "text-slate-800";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{label}</div>
      <div className={`font-display text-2xl font-semibold tabular-nums mt-0.5 print:text-[16px] ${toneCls}`}>{value}</div>
    </div>
  );
}

function EmptyState({ companyName }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-white p-14 text-center" data-testid="org-empty">
      <Network className="h-9 w-9 text-[#C5A059] mx-auto mb-3" />
      <div className="font-display text-xl font-semibold">Nenhum setor encontrado</div>
      <p className="mt-2 text-[13px] text-muted-foreground max-w-[420px] mx-auto">
        {companyName
          ? `A empresa "${companyName}" não possui setores cadastrados ainda.`
          : "Cadastre setores e atribua um Setor Pai para gerar o organograma."}
      </p>
    </div>
  );
}
